import { canExploreSignupDashboard } from '@/components/signup/activation';
import axios from 'axios';
import {
  businessDraftsReplies,
  handoffKeepsDrafting,
} from '@/components/ai-employee/components/handoff-ai-state';
import {
  currentReplyMode,
  isAiReplyOn,
  replyModesFrom,
} from '@/components/ai-employee/components/reply-modes';
import { isAgreementRequiredError } from '@/components/legal/AgreementAcceptanceDialog';
import {
  Bot,
  Check,
  CheckCircle,
  Ellipsis,
  EllipsisVertical,
  FolderMinus,
  FolderPlus,
  FolderSymlink,
  Mail,
  MailCheck,
  MailOpen,
  MailWarning,
  MessageSquareOff,
  PanelRightOpen,
  Pencil,
  PowerOff,
  Phone,
  Pin,
  RotateCcw,
  Tag,
  TriangleAlert,
  User,
  UserRound,
  Users,
} from 'lucide-react';
import { Component } from 'react';
import { connect } from 'react-redux';
import { toast } from 'sonner';
import store from 'store';
import { addFolderToConversation, removeFolderFromConversation } from '@/actions/folders';
import { displayNotification } from '@/actions/settings';
import { toggleCustomerTag } from '@/actions/tags';
import {
  resolveConversationEscalation,
  toggleConversationAiPause,
  toggleConversationOpen,
  toggleConversationPin,
  updateConversationAssignees,
  updateLoadedCustomerConversationTags,
} from '@/actions/webchat';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/custom/IconButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toastPosition } from '@/components/notifications/toastPosition';
import { Input } from '@/components/ui/input';
import {
  ActionSheet,
  MobileBackButton,
  MobileHeaderButton,
} from '@/components/mobile';
import { getCustomerConversationBasePath } from '@/utils/customerConversationRoutes';
import { MultiSelect } from '@/components/ui/custom/multi-select';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { setCustomer, setStatus, setVisible } from '@/reducers/voiceReducer';
import { removeAiEscalation } from '@/slices/aiEmployeeSlice';
import { isCustomerOnActiveCall } from '@/utils/activeCustomerCall';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';
import getAvatarColor from '@/utils/getAvatarColor';
import formatPhoneForTitleBar from '../../../utils/formatPhoneForUser';
import getWebchatApiUrl from '../../../utils/getWebchatUrl';
import { generatedResponseFrom } from './aiDraft';
import { maskPhone } from '@/utils/maskPhone';
import ChatBox from './ChatBox';
import ChatFeed from './Chatfeed/ChatFeed';
import CreateFolderDialog from './CreateFolderDialog';
import InboxOverview from './InboxOverview';

// The AI's reply mode depends on whether the business is open, so a cached copy of its
// settings older than this may predate opening or closing time.
const AI_SETTINGS_MAX_AGE_MS = 5 * 60 * 1000;

function conversationBusinessId(conversation) {
  const rawBiz = conversation?._business;
  return typeof rawBiz === 'object' ? rawBiz?._id : rawBiz;
}

// Matches the max-xl: header styles: below xl the actions stay on one line and
// the assignee chip shrinks, so the bar never wraps a lone button onto a row.
const NOWRAP_ACTIONS_QUERY = '(max-width: 1279px)';

// A phone held sideways (about 340–430px tall, 110–200px with the keyboard
// up): the header keeps its height, the composer takes what it needs from the
// rest, and the thread gets whatever is left over. A composer taller than
// that (an open payment form) scrolls inside itself instead of pushing the
// field and Send off screen or under the keyboard. Rows: header, thread,
// composer.
const SIDEWAYS_ROWS =
  'max-xl:[@media(max-height:560px)]:grid max-xl:[@media(max-height:560px)]:grid-cols-[minmax(0,1fr)] max-xl:[@media(max-height:560px)]:grid-rows-[auto_minmax(0,1fr)_minmax(0,auto)]';

// The phone header's banner buttons only show while the keyboard is up, so
// they hide again as soon as focus leaves the field and can't take focus back.
// Once the full banner shows, focus it, unless focus has already settled on
// something else on screen.
function focusBannerOnceShown(getBanner, timeout = 2000) {
  let until = performance.now() + timeout;
  const step = () => {
    const active = document.activeElement;
    const lost = !active || active === document.body || active.getClientRects().length === 0;
    const banner = getBanner();
    if (lost && banner?.getClientRects().length) {
      banner.focus({ preventScroll: true });
      return;
    }
    // Still waiting for the keyboard to drop, or still in the closing sheet
    // or a confirm it opened (Reset AI memory). The confirm hands focus back
    // to the hidden header button when it closes, so wait while it's open.
    const inDialog = active?.closest('[role="dialog"], [role="alertdialog"]');
    if (inDialog) until = performance.now() + timeout;
    if ((lost || inDialog) && performance.now() < until) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
}

class Messages extends Component {
  constructor(props) {
    super(props);

    const oldCustomerInput = store.get('oldCustomerInput');

    if (oldCustomerInput) {
      store.set('sharedComputer', oldCustomerInput);
    }

    const sharedComputer = store.get('sharedComputer');

    this.state = {
      name: '',
      phoneEmail: '',
      _location: '', // for enterprise, we need to send the _business sending the message
      employee: null,
      errorMessage: '',
      sharedComputer: !!sharedComputer,
      isAssigning: false,
      assigneeSelectionOverride: null, // local UI selection to avoid ref recursion
      hasVoiceProduct: this.props.auth.products?.includes('voice'),
      showVoiceUpsell: false,
      aiSettings: null,
      aiReplyStatus: null,
      aiSettingsBusinessId: null,
      aiSettingsFetchedAt: 0,
      showCreateFolderDialog: false,
      showResetAiDialog: false,
      assigneeMaxVisibleCount: 1,
      assigneeMaxWidth: 200,
      // Phones: the header's "…" sheet and the group rename dialog.
      mobileActionsOpen: false,
      mobileAiOpen: false,
      mobileRenameOpen: false,
    };

    this.nameAndPhoneBarRef = null;
    this.chatIdentityRef = null;
    this.assigneeActionsRef = null;
    this.assigneeTriggerRef = null;
    this.assigneeResizeObserver = null;
    this.assigneeObservedElements = new Set();
    this.assigneeMeasureFrame = null;
    this.assigneeMeasureCanvasContext = null;
  }

  componentDidMount() {
    this.fetchAiSettings();
    window.addEventListener('ai-settings-changed', this.handleAiSettingsChanged);
    this.setupAssigneeBadgeMeasurement();
    this.queueAssigneeBadgeMeasurement();
    this.fetchGroupSmsStatus();
  }

  // gates the "Add people" action (group SMS must be provisioned)
  fetchGroupSmsStatus = async () => {
    try {
      const res = await axios.get(
        `${getWebchatApiUrl(window.location.href)}/api/webchat/group-sms/status`,
        { withCredentials: true },
      );
      if (!res.data?.error) {
        this.setState({ groupSmsEnabled: !!res.data.enabled });
      }
    } catch {
      // leave disabled
    }
  };

  handleAddPeople = () => {
    const { activeConversation } = this.props;
    const customer = activeConversation?._customer;

    if (!customer?.phone || !this.props.navigate) {
      return;
    }

    this.props.navigate('/inbox/new', {
      state: {
        prefillRecipient: {
          key: customer._id,
          _customer: customer._id,
          _business: activeConversation._business,
          name: customer.name || customer.phone,
          phoneEmail: customer.phone,
          isNew: false,
        },
      },
    });
  };

  componentDidUpdate(prevProps) {
    if (this.props.messages.length !== prevProps.messages.length) {
      this.setState({ phoneEmail: '', name: '' });
    }

    const currentBiz =
      typeof this.props.activeConversation?._business === 'object'
        ? this.props.activeConversation._business._id
        : this.props.activeConversation?._business;
    const prevBiz =
      typeof prevProps.activeConversation?._business === 'object'
        ? prevProps.activeConversation._business._id
        : prevProps.activeConversation?._business;
    // Another conversation of the same business re-reads the settings only once they are stale.
    if (
      currentBiz &&
      (currentBiz !== prevBiz ||
        this.props.activeConversation?._id !== prevProps.activeConversation?._id)
    ) {
      this.fetchAiSettings();
    }

    if (this.props.activeConversation?._id !== prevProps.activeConversation?._id) {
      this.setState({ assigneeSelectionOverride: null });
    }

    this.observeAssigneeBadgeElements();
    this.queueAssigneeBadgeMeasurement();
  }

  componentWillUnmount() {
    window.removeEventListener('ai-settings-changed', this.handleAiSettingsChanged);

    if (this.assigneeResizeObserver) {
      this.assigneeResizeObserver.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.queueAssigneeBadgeMeasurement);
    }

    if (typeof window !== 'undefined' && this.assigneeMeasureFrame) {
      window.cancelAnimationFrame(this.assigneeMeasureFrame);
    }
  }

  setupAssigneeBadgeMeasurement = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if ('ResizeObserver' in window) {
      this.assigneeResizeObserver = new ResizeObserver(() => {
        this.queueAssigneeBadgeMeasurement();
      });
      this.observeAssigneeBadgeElements();
    }

    window.addEventListener('resize', this.queueAssigneeBadgeMeasurement);
  };

  observeAssigneeBadgeElements = () => {
    if (!this.assigneeResizeObserver) {
      return;
    }

    [
      this.nameAndPhoneBarRef,
      this.chatIdentityRef,
      this.assigneeActionsRef,
      this.assigneeTriggerRef,
    ].forEach((element) => {
      if (element && !this.assigneeObservedElements.has(element)) {
        this.assigneeResizeObserver.observe(element);
        this.assigneeObservedElements.add(element);
      }
    });
  };

  queueAssigneeBadgeMeasurement = () => {
    if (typeof window === 'undefined' || this.assigneeMeasureFrame) {
      return;
    }

    this.assigneeMeasureFrame = window.requestAnimationFrame(() => {
      this.assigneeMeasureFrame = null;
      this.updateAssigneeBadgeDisplay();
    });
  };

  getAssigneeSelection = () => {
    const { activeConversation, users = [], departments = [] } = this.props;
    const { _business } = activeConversation || {};

    if (!_business) {
      return {
        options: [],
        selectedValues: [],
        assigneeNames: [],
      };
    }

    const activeUsers = users.filter((user) => user.active && user._businesses.includes(_business));

    const userOptions = activeUsers.map((user) => ({
      label: user.name || user.username,
      value: `USER_${user._id}`,
    }));

    const departmentOptions = departments
      .filter((d) => d._business === _business)
      .map((d) => ({
        label: d.name,
        value: `DEPARTMENT_${d._id}`,
        // Maybe add an icon for departments if available, or just rely on grouping
      }));

    const options = [
      {
        heading: 'Users',
        options: userOptions,
      },
      {
        heading: 'Departments',
        options: departmentOptions,
      },
    ];

    let selectedValues =
      this.state.assigneeSelectionOverride || this.getSelectedAssigneesFromProps();

    if (!Array.isArray(selectedValues)) {
      selectedValues = [];
    }

    const allOptions = [...userOptions, ...departmentOptions];

    // Drop selections that no longer have a matching option (e.g. inactive users),
    // so the selector falls back to the "Unassigned" placeholder instead of a blank badge.
    selectedValues = selectedValues.filter((v) => allOptions.some((o) => o.value === v));

    const assigneeNames = selectedValues
      .map((v) => allOptions.find((o) => o.value === v)?.label)
      .filter(Boolean);

    return {
      options,
      selectedValues,
      assigneeNames,
    };
  };

  getAvailableAssigneeWidth = () => {
    if (!this.nameAndPhoneBarRef || !this.assigneeActionsRef || !this.assigneeTriggerRef) {
      return 0;
    }

    const barRect = this.nameAndPhoneBarRef.getBoundingClientRect();
    const actionsRect = this.assigneeActionsRef.getBoundingClientRect();
    const triggerRect = this.assigneeTriggerRef.getBoundingClientRect();
    const identityRect = this.chatIdentityRef?.getBoundingClientRect();
    // Below xl the actions never wrap, so their box can be squeezed; measure the
    // other actions themselves there.
    const compactRow =
      typeof window !== 'undefined' && window.matchMedia(NOWRAP_ACTIONS_QUERY).matches;
    const nonAssigneeActionsWidth = compactRow
      ? Array.from(this.assigneeActionsRef.children).reduce((total, child) => {
          if (child.contains(this.assigneeTriggerRef)) return total;
          const style = window.getComputedStyle(child);
          return (
            total +
            child.getBoundingClientRect().width +
            (parseFloat(style.marginLeft) || 0) +
            (parseFloat(style.marginRight) || 0) +
            2
          );
        }, 0)
      : Math.max(actionsRect.width - triggerRect.width, 0);
    const actionsWrapped = identityRect && actionsRect.top > identityRect.bottom - 4;
    const rowWidth =
      identityRect && !actionsWrapped
        ? barRect.right - identityRect.right - 8
        : barRect.width;

    return Math.max(120, rowWidth - nonAssigneeActionsWidth);
  };

  getAssigneeMeasureContext = () => {
    if (typeof document === 'undefined') {
      return null;
    }

    if (!this.assigneeMeasureCanvasContext) {
      this.assigneeMeasureCanvasContext = document.createElement('canvas').getContext('2d');
    }

    if (!this.assigneeMeasureCanvasContext) {
      return null;
    }

    const triggerButton = this.assigneeTriggerRef?.querySelector('button');
    const font = triggerButton ? window.getComputedStyle(triggerButton).font : '14px sans-serif';
    this.assigneeMeasureCanvasContext.font = font;
    return this.assigneeMeasureCanvasContext;
  };

  getEstimatedAssigneeBadgeWidth = (label) => {
    const context = this.getAssigneeMeasureContext();
    const textWidth = context ? context.measureText(label).width : label.length * 8;

    return Math.ceil(textWidth + 44);
  };

  getEstimatedAssigneeDisplayWidth = (assigneeNames, visibleCount, selectedCount) => {
    const badgeLabels = assigneeNames.slice(0, visibleCount);

    if (selectedCount > visibleCount) {
      badgeLabels.push(`+ ${selectedCount - visibleCount} more`);
    }

    const badgeWidth = badgeLabels.reduce(
      (total, label) => total + this.getEstimatedAssigneeBadgeWidth(label),
      0,
    );
    const gapWidth = Math.max(badgeLabels.length - 1, 0) * 4;

    return badgeWidth + gapWidth + 40;
  };

  updateAssigneeBadgeDisplay = () => {
    const { selectedValues, assigneeNames } = this.getAssigneeSelection();
    const selectedCount = selectedValues.length;
    const maxCandidate = Math.min(Math.max(selectedCount, 1), 3);
    const availableWidth = this.getAvailableAssigneeWidth();
    let nextMaxVisibleCount = 1;

    for (let count = maxCandidate; count > 1; count -= 1) {
      if (
        this.getEstimatedAssigneeDisplayWidth(assigneeNames, count, selectedCount) <=
        availableWidth
      ) {
        nextMaxVisibleCount = count;
        break;
      }
    }

    if (selectedCount <= 1) {
      nextMaxVisibleCount = 1;
    }

    const nextMaxWidth = Math.ceil(availableWidth || 200);

    if (
      this.state.assigneeMaxVisibleCount !== nextMaxVisibleCount ||
      this.state.assigneeMaxWidth !== nextMaxWidth
    ) {
      this.setState({
        assigneeMaxVisibleCount: nextMaxVisibleCount,
        assigneeMaxWidth: nextMaxWidth,
      });
    }
  };

  renderChatFeed() {
    return (
      <ChatFeed
        innerRef={this.props.chatFeedRef}
        messages={this.props.messages}
        loadingNewMessage={this.props.loadingNewMessage}
        activeConversation={this.props.activeConversation}
        addConversationToCustomerConversations={this.props.addConversationToCustomerConversations}
        scheduledMessagesRefreshKey={this.props.scheduledMessagesRefreshKey}
        refreshScheduledMessages={this.props.refreshScheduledMessages}
        registerScheduledMessageResolved={this.props.registerScheduledMessageResolved}
        isMobile={!!this.props.isMobile}
      />
    );
  }

  getEmployeeName = () => localStorage.getItem('employeeName') || this.state.employee || '';

  saveCustomer = () => {
    const { name, phoneEmail, _location } = this.state;
    this.props.saveCustomer({ name, phoneEmail, _location });
  };

  onNameChange = (name) => {
    this.setState({ name });
  };

  onLocationChange = (_location) => {
    this.setState({ _location });
  };

  generateAIResponse = async () => {
    const messages = this.props.messages
      .filter((m) => m.text && m.sender !== 'event')
      .map((m) => ({
        sender: m.sender,
        text: m.text,
        createdAt: m.createdAt,
      }));

    try {
      const res = await axios.post(
        '/api/ai/generate-response',
        { messages },
        { timeout: 25000 },
      );

      // { response, staffNote }: response is '' when the AI could not answer and
      // only left a note for the team (AIEnhanceButton shows it).
      return generatedResponseFrom(res.data) || { response: 'error' };
    } catch (err) {
      if (isAgreementRequiredError(err)) {
        return {
          response: 'error',
          agreementRequired: err.response.data.requiredAgreements,
        };
      }

      return { response: 'error' };
    }
  };

  onEmployeeChange = (e) => {
    localStorage.setItem('employeeName', e.target.value);
    this.setState({ employee: e.target.value });
  };

  resetNameAndPhoneEmail = () => {
    const el = document.getElementById('chatBoxTextBox');

    // Phones: focusing would pop the keyboard back up after a send from a sheet.
    if (el && !this.props.isMobile) {
      el.focus();
    }

    this.setState({ phoneEmail: '', name: '', errorMessage: '' });
  };

  openConversation = (conversation) => {
    // if it's already open do nothing
    if (conversation.open) {
      return;
    }
    this.props.toggleConversationOpen({ conversation, open: true });
  };

  closeConversation = (conversation) => {
    // if it's already closed do nothing
    if (!conversation.open) {
      return;
    }

    this.props.toggleConversationOpen({ conversation, open: false });
  };

  markAsReadConversation = (conversation) => {
    if (conversation.isRead) {
      return;
    }

    this.props.toggleMarkAsReadConversation({ conversation, isRead: true });
  };

  markAsUnreadConversation = (conversation) => {
    if (!conversation.isRead) {
      return;
    }

    this.props.toggleMarkAsReadConversation({
      conversation,
      isRead: false,
    });
  };

  handleToggleConversationPin = async ({ scope, pinned }) => {
    const res = await this.props.toggleConversationPin({
      conversation: this.props.activeConversation,
      scope,
      pinned,
    });

    if (res?.error) {
      this.props.displayNotification({
        message: res.message || 'Error updating conversation pin',
        error: true,
      });
    }
  };

  handleChangeCustomerInputType = () => {
    const { sharedComputer } = this.state;
    if (this.state.sharedComputer) {
      this.setState({ employee: '' });
      localStorage.setItem('employeeName', '');
    }
    this.setState({ sharedComputer: !sharedComputer });
    store.set('sharedComputer', !sharedComputer);
  };

  renderNewMessage = () => (
    <InboxOverview handleCustomerDialogIsOpen={this.props.handleCustomerDialogIsOpen} />
  );

  startEditChatName = () => {
    this.setState({
      isEditingChatName: true,
      chatNameDraft: this.props.activeConversation?.chatName || '',
    });
  };

  saveChatName = async () => {
    const { activeConversation } = this.props;
    const chatName = (this.state.chatNameDraft || '').trim();
    this.setState({ isEditingChatName: false });

    if (chatName === (activeConversation.chatName || '')) return;

    try {
      const res = await axios.post(
        `${getWebchatApiUrl(window.location.href)}/api/webchat/group-sms/chat-name`,
        { _customerConversation: activeConversation._id, chatName },
        { withCredentials: true },
      );

      if (!res.data?.error && res.data?.customerConversation) {
        // keep the loaded messages — the endpoint returns the conversation only
        this.props.addConversationToCustomerConversations(
          { ...res.data.customerConversation, messages: activeConversation.messages || [] },
          true,
        );
      }
    } catch (err) {
      console.error('Failed to save chat name', err);
    }
  };

  renderGroupChatTitle = () => {
    const { activeConversation, isActivated } = this.props;
    const customers = activeConversation._customers || [];
    const displayName =
      activeConversation.chatName ||
      customers.map((c) => maskPhone(c?.name || c?.phone, isActivated)).filter(Boolean).join(', ') ||
      maskPhone(activeConversation.title, isActivated) ||
      'Group';
    const phones = customers
      .map((c) => (c?.phone ? maskPhone(formatPhoneForTitleBar(c.phone), isActivated) : null))
      .filter(Boolean);

    return (
      <div className="conversation-enter flex min-w-0 flex-1 items-center gap-3" key={activeConversation._id}>
        <Avatar className="size-9 shrink-0">
          <AvatarFallback
            className="text-sm text-white font-medium"
            style={{ backgroundColor: getAvatarColor(displayName || activeConversation._id) }}
          >
            <Users className="size-4" />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col justify-center gap-0.5 min-w-0">
          {this.state.isEditingChatName ? (
            <input
              // biome-ignore lint/a11y/noAutofocus: rename input appears on explicit user action
              autoFocus
              value={this.state.chatNameDraft}
              placeholder="Name this group chat"
              className="text-sm font-medium bg-transparent border-b border-input outline-none max-w-[260px]"
              onChange={(e) => this.setState({ chatNameDraft: e.target.value })}
              onBlur={this.saveChatName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') this.saveChatName();
                if (e.key === 'Escape') this.setState({ isEditingChatName: false });
              }}
            />
          ) : (
            <span className="group font-medium text-[15px] leading-5 truncate flex items-center gap-1.5">
              <span className="truncate">{displayName}</span>
              <button
                type="button"
                aria-label="Rename group chat"
                className="text-muted-foreground hover:text-foreground shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 touch:opacity-100 transition-opacity"
                onClick={this.startEditChatName}
              >
                <Pencil className="size-3" />
              </button>
            </span>
          )}
          <span className="text-xs text-muted-foreground truncate">
            {customers.length} people{phones.length ? ` · ${phones.join(', ')}` : ''}
          </span>
        </div>
      </div>
    );
  };

  renderChatTitle = () => {
    const { activeConversation } = this.props;

    if (activeConversation?.isGroup) {
      return this.renderGroupChatTitle();
    }

    const { title, _customer } = activeConversation || {};

    const name = maskPhone(title || activeConversation._customer?.name || 'No name', this.props.isActivated);
    const customerOnActiveCall = isCustomerOnActiveCall(
      this.props.voice.ongoingCalls,
      _customer,
    );
    const callDisabled =
      customerOnActiveCall ||
      ['connecting', 'ringing', 'answered'].includes(this.props.voice.status);

    let service = '';
    let isPhone = false;

    if (_customer?.phone || _customer?.preferredChannel === 'phone') {
      service = maskPhone(formatPhoneForTitleBar(_customer?.phone), this.props.isActivated);
      isPhone = true;
    }

    if (
      (_customer?.email && !_customer?.phone) ||
      (_customer?.email && _customer?.preferredChannel === 'email')
    ) {
      service = _customer?.email;
      isPhone = false;
    }

    if (_customer?.facebookUserId && !_customer?.phone) {
      service = 'Facebook Messenger';
      isPhone = false;
    }

    if (_customer?.instagramUserId && !_customer?.phone) {
      service = 'Instagram';
      isPhone = false;
    }

    if (_customer?.googleConversationId && !_customer?.phone) {
      service = 'Google Business Messenger';
      isPhone = false;
    }

    return (
      <div className="conversation-enter flex min-w-0 flex-1 items-center gap-3" key={activeConversation._id}>
        <CustomerAvatar
          customer={_customer && {
            ..._customer,
            _business: _customer._business || activeConversation._business,
          }}
          className="size-9 shrink-0"
          fallbackClassName="text-sm font-medium"
          fallbackName={name}
          colorSeed={
            _customer?.name || activeConversation.title || _customer?._id || activeConversation._id
          }
        />
        <div className="flex flex-col justify-center gap-0.5 min-w-0">
          <span className="font-medium text-[15px] leading-5 truncate">{name || 'No name'}</span>
          {isPhone ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={callDisabled}
                  onClick={() => {
                    if (this.state.hasVoiceProduct) {
                      this.callCustomer();
                    } else {
                      this.setState({
                        showVoiceUpsell: true,
                      });
                    }
                  }}
                  className="text-xs text-muted-foreground truncate leading-tight text-left hover:underline cursor-pointer disabled:cursor-not-allowed disabled:no-underline"
                >
                  {service}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {customerOnActiveCall
                  ? 'Customer is already on a call'
                  : `Call ${name || 'customer'}`}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-xs text-muted-foreground truncate leading-tight">{service}</span>
          )}
        </div>
      </div>
    );
  };

  // Loads the active business's AI settings: both reply modes and, from the server, the
  // mode in effect now (replyStatus). Cached per business until the copy is stale or
  // `force` is set. Resolves to { ai, replyStatus }, or null without the AI product.
  fetchAiSettings = ({ force = false } = {}) => {
    if (!hasAiEmployeeProduct(this.props.auth)) return Promise.resolve(null);
    const businessId = conversationBusinessId(this.props.activeConversation);
    if (!businessId) return Promise.resolve(null);

    const fresh =
      businessId === this.state.aiSettingsBusinessId &&
      Date.now() - this.state.aiSettingsFetchedAt < AI_SETTINGS_MAX_AGE_MS;
    if (fresh && !force) {
      return Promise.resolve({ ai: this.state.aiSettings, replyStatus: this.state.aiReplyStatus });
    }
    if (!force && this.aiSettingsRequest?.businessId === businessId) {
      return this.aiSettingsRequest.promise;
    }

    // This business's current copy, kept if the refresh fails (e.g. during an ai-employee deploy).
    const cachedCopy = () =>
      businessId === this.state.aiSettingsBusinessId && this.state.aiSettings
        ? { ai: this.state.aiSettings, replyStatus: this.state.aiReplyStatus }
        : null;
    const cachedAtStart = cachedCopy();

    // Background check for the AI status chip; a failure keeps the cached copy
    // or hides the chip, so don't show an error toast in the inbox.
    const promise = axios
      .get(`/api/ai-employee/settings/${businessId}`, { _suppressErrorToast: true })
      .then(
        (res) => ({ ai: res.data?.ai || null, replyStatus: res.data?.replyStatus || null }),
        () => null,
      )
      .then((result) => {
        // Only the latest request updates the inbox, and only while its business is still open.
        const latest = this.aiSettingsRequest?.promise === promise;
        if (latest) this.aiSettingsRequest = null;
        if (!result) {
          // A failed refresh keeps the good copy and its age, so the next use retries.
          // Only with nothing cached for this business is it stored as "no settings".
          const kept = cachedCopy() || cachedAtStart;
          if (kept) return kept;
        }
        const loaded = result || { ai: null, replyStatus: null };
        if (latest && conversationBusinessId(this.props.activeConversation) === businessId) {
          this.setState({
            aiSettings: loaded.ai,
            aiReplyStatus: loaded.replyStatus,
            aiSettingsBusinessId: businessId,
            aiSettingsFetchedAt: Date.now(),
          });
        }
        return loaded;
      });
    this.aiSettingsRequest = { businessId, promise };
    return promise;
  };

  handleAiSettingsChanged = () => {
    this.fetchAiSettings({ force: true });
  };

  toggleAiPause = () => {
    const conv = this.props.activeConversation;
    if (!conv) return;

    // Switching the AI off here is a hold, asked for by name so that it replaces
    // a pause the screen has not caught up with.
    this.props.toggleConversationAiPause({
      conversation: conv,
      aiPaused: !conv.aiPaused,
      ...(conv.aiPaused ? {} : { reason: 'manual_hold' }),
    });
  };

  // When a human replies to the customer, pause the AI support agent so it
  // doesn't respond on top of the user. Only applies when the mode in effect now
  // (open or closed hours) replies autonomously ('auto'/'delayed') — 'assist' only
  // drafts suggestions for the human, so pausing it would just remove a helper.
  // No-op when the AI is off or already paused.
  autoPauseAiForUserReply = ({ conversation, mode }) => {
    if (!hasAiEmployeeProduct(this.props.auth)) return;

    if (mode !== 'auto' && mode !== 'delayed') return;

    if (!conversation?._id) return;

    const didPause = this.props.toggleConversationAiPause({
      conversation,
      aiPaused: true,
      reason: 'human_reply',
    });
    if (!didPause) return;

    toast('AI paused for the conversation you replied to', {
      description:
        'It stays out while this conversation is open. Once it is closed and nobody on your team has written to this customer for 48 hours, the AI answers the next time they write. You can switch it back on anytime from the AI status.',
      position: toastPosition('bottom-right'),
    });
  };

  sendMessage = async (message) => {
    // Capture the send context before awaiting. The active conversation and
    // business AI settings can change if the user switches threads mid-send.
    const sentConversation = this.props.activeConversation;
    // A stale copy of the AI settings is refreshed alongside the send.
    const sentConversationAiSettings = this.fetchAiSettings();

    // send message
    const sendMessageRes = await this.props.sendMessage(message);

    const { activeConversation } = this.props;

    // if the conversation is not assigned, assign it to the current user
    const hasAssignees = activeConversation._assignees && activeConversation._assignees.length > 0;

    if (!hasAssignees && !activeConversation._assignee) {
      this.handleSelectAssignees([`USER_${this.props.user._id}`]);
    }

    // Only pause for an immediate reply — a scheduled message isn't the user
    // handling the conversation right now, so the AI should stay on until then.
    // The send doesn't wait for the settings: the pause runs once they arrive, with the
    // sent conversation's business settings captured before sending.
    if (!sendMessageRes?.error && !message?.scheduledSendDate) {
      sentConversationAiSettings.then((aiSettings) =>
        this.autoPauseAiForUserReply({
          conversation: sentConversation,
          mode: aiSettings ? currentReplyMode(aiSettings.ai, aiSettings.replyStatus) : undefined,
        }),
      );
    }

    return sendMessageRes;
  };

  sendMessageToNewCustomer = async (message) => {
    const sendMessageRes = await this.props.sendMessageToNewCustomer(message);
    return sendMessageRes;
  };

  // Notes are internal and shouldn't auto-assign the conversation, so this is a
  // thin passthrough (unlike sendMessage above).
  sendNote = async (note) => {
    return this.props.sendNote(note);
  };

  getSelectedAssigneesFromProps = () => {
    const { activeConversation } = this.props;
    const { _assignees = [], _assignee, _department } = activeConversation;

    if (_department) {
      return [`DEPARTMENT_${_department}`];
    }
    if (_assignees && _assignees.length > 0) {
      return _assignees.map((id) => `USER_${id}`);
    }
    if (_assignee) {
      return [`USER_${_assignee}`];
    }
    return [];
  };

  handleSelectAssignees = async (selectedValues) => {
    try {
      const { activeConversation } = this.props;

      const prevSelections =
        this.state.assigneeSelectionOverride || this.getSelectedAssigneesFromProps();

      // Figure out what was newly added
      const addedSelections = selectedValues.filter((v) => !prevSelections.includes(v));
      const latestAdded =
        addedSelections.length > 0 ? addedSelections[addedSelections.length - 1] : null;

      let sanitizedSelections = [];

      if (latestAdded) {
        if (latestAdded.startsWith('DEPARTMENT_')) {
          // Department wins: keep only that department
          sanitizedSelections = [latestAdded];
        } else if (latestAdded.startsWith('USER_')) {
          // User selected: drop any departments, keep users
          sanitizedSelections = selectedValues.filter((v) => v.startsWith('USER_'));
        }
      } else {
        // No addition (likely removal). Honor existing selection but enforce exclusivity.
        const departmentValues = selectedValues.filter((v) => v.startsWith('DEPARTMENT_'));
        if (departmentValues.length > 0) {
          sanitizedSelections = [departmentValues[departmentValues.length - 1]];
        } else {
          sanitizedSelections = selectedValues.filter((v) => v.startsWith('USER_'));
        }
      }

      // Derive payload
      let isDepartment = false;
      let _assignedUser = 'Unassigned';
      let userIds = [];

      if (sanitizedSelections.length === 1 && sanitizedSelections[0].startsWith('DEPARTMENT_')) {
        isDepartment = true;
        _assignedUser = sanitizedSelections[0].replace('DEPARTMENT_', '');
      } else {
        userIds = sanitizedSelections
          .filter((v) => v.startsWith('USER_'))
          .map((v) => v.replace('USER_', ''));
        if (userIds.length === 0) {
          _assignedUser = 'Unassigned';
        }
      }

      // Store sanitized selection locally to keep UI in sync without ref callbacks
      this.setState({ assigneeSelectionOverride: sanitizedSelections });

      this.setState({ isAssigning: true });

      if (isDepartment) {
        // Assign to department
        await axios.post(`${getWebchatApiUrl(window.location.href)}/api/team/assign-conversation`, {
          isDepartment: true,
          _assignedUser,
          _customerConversation: activeConversation._id,
        });

        // Update Redux state so CustomerList shows the same value
        this.props.updateConversationAssignees({
          _conversation: activeConversation._id,
          _assignees: [],
          _department: _assignedUser,
        });
      } else {
        // Assign to users (or unassign if empty)
        await axios.post(`${getWebchatApiUrl(window.location.href)}/api/team/assign-conversation`, {
          isDepartment: false,
          _assignees: userIds,
          _customerConversation: activeConversation._id,
        });

        // Update Redux state so CustomerList shows the same value
        this.props.updateConversationAssignees({
          _conversation: activeConversation._id,
          _assignees: userIds,
          _department: null,
        });
      }

      this.setState({ isAssigning: false });
    } catch (err) {
      console.log(err);
      this.setState({ isAssigning: false });
    }
  };

  handleSelectPreferredChannel = async (channel) => {
    try {
      const preferredChannel = channel;

      const { activeConversation } = this.props;

      await axios.post(
        `${getWebchatApiUrl(window.location.href)}/api/conversation/set-preferred-channel`,
        {
          preferredChannel,
          _customer: activeConversation._customer._id,
          _business: activeConversation._business,
        },
      );
    } catch (err) {
      console.log(err);
    }
  };

  renderSelectAssignee = () => {
    const { activeConversation } = this.props;
    const { options, selectedValues, assigneeNames } = this.getAssigneeSelection();
    const tooltipText = assigneeNames.length
      ? `Assigned to ${assigneeNames.join(', ')}`
      : 'Unassigned';
    const maxVisibleAssigneeBadges = Math.min(
      Math.max(this.state.assigneeMaxVisibleCount, 1),
      Math.max(selectedValues.length, 1),
    );

    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div
            ref={(node) => {
              this.assigneeTriggerRef = node;
            }}
            data-testid="chat-assignee-dropdown-trigger"
            className="max-xl:min-w-0 max-xl:[&>button]:max-w-full"
          >
            <MultiSelect
              key={activeConversation?._id || 'default'}
              options={options}
              value={selectedValues}
              onValueChange={this.handleSelectAssignees}
              placeholder="Unassigned"
              placeholderIcon={<User className="size-3.5 text-muted-foreground" />}
              className="min-h-8 min-w-0 border-none shadow-none px-1.5 py-1 text-xs transition-none hover:bg-accent rounded-md max-xl:[&>div]:min-w-0 max-xl:[&>div>div:first-child]:min-w-0 max-xl:[&>div>div:first-child>span]:min-w-0 max-xl:[&>div>div:first-child>span]:shrink max-xl:[&>div>div:first-child>span>span]:truncate"
              autoSize
              singleLine
              hideSelectAll
              closeOnSelect
              maxCount={maxVisibleAssigneeBadges}
              maxWidth={`${this.state.assigneeMaxWidth}px`}
              loading={this.state.isAssigning}
              animationConfig={{
                badgeAnimation: 'none',
                duration: 0,
                delay: 0,
                popoverAnimation: 'none',
                optionHoverAnimation: 'none',
              }}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    );
  };

  renderErrorWithCustomer = () => {
    const { activeConversation } = this.props;

    // group conversations have _customers instead of _customer — not an error
    if (activeConversation.isGroup) {
      return null;
    }

    if (!activeConversation._customer) {
      return (
        <div>
          <IconButton
            className="hover:bg-transparent!"
            toolTipTitle="There may be an error with this customer. Please refresh the page or contact support."
            toolTipSide="bottom"
            toolTipClassName={'w-3/12'}
            toolTipAlign="start"
          >
            <TriangleAlert className="size-4 text-destructive" />
          </IconButton>
        </div>
      );
    }

    if (
      typeof activeConversation._customer.active === 'boolean' &&
      activeConversation._customer.active === false
    ) {
      return (
        <div>
          <IconButton
            className="hover:bg-transparent!"
            toolTipTitle="This customer has opted out of receiving
						messages from you. Have them text START to your
						texting number to resume receiving messages."
            toolTipSide="bottom"
            toolTipClassName={'w-3/12'}
            toolTipAlign="start"
          >
            <TriangleAlert className="size-4 text-destructive" />
          </IconButton>
        </div>
      );
    }

    return null;
  };

  renderEnterpriseBusinessInfo = () => {
    const { activeConversation, businessGroup, auth } = this.props;

    if (!auth.user.isEnterpriseEnabled) return null;

    const { _business } = activeConversation;

    const business = businessGroup.filter((b) => b._id === _business)[0];

    if (business) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginRight: 10,
          }}
        >
          <p
            style={{
              display: 'flex',
              flexDirection: 'column',
              paddingLeft: 4,
            }}
          >
            <span>
              {business.name.length > 20 ? `${business.name.slice(0, 20)}...` : business.name}
            </span>
            <span>{formatPhoneForTitleBar(business.twilioNumber)}</span>
          </p>
        </div>
      );
    }

    return null;
  };

  handleFolderAction = async (_folder) => {
    const { activeConversation } = this.props;
    if (activeConversation._folder === _folder) {
      await this.handleRemoveFolder(_folder);
    } else {
      const { folders } = this.props;
      if (!folders.find((f) => f._id === _folder)) return;

      this.props.addFolderToCustomerConversationLocally({
        _folder,
        _conversation: activeConversation._id,
      });

      const res = await this.props.addFolderToConversation({
        _folder,
        _conversation: activeConversation._id,
        _business: activeConversation._business,
      });

      if (res.error) {
        this.props.displayNotification({
          message: 'Error adding conversation to folder',
          error: true,
        });
        this.props.removeFolderFromCustomerConversationLocally({
          _conversation: activeConversation._id,
        });
      }
    }
  };

  handleRemoveFolder = async (_folder) => {
    const { activeConversation } = this.props;

    this.props.removeFolderFromCustomerConversationLocally({
      _conversation: activeConversation._id,
    });

    const res = await this.props.removeFolderFromConversation({
      _conversation: activeConversation._id,
      _business: activeConversation._business,
    });

    if (res.error) {
      this.props.displayNotification({
        message: 'Error removing conversation from folder',
        error: true,
      });
      this.props.addFolderToCustomerConversationLocally({
        _folder,
        _conversation: activeConversation._id,
      });
    }
  };

  handleToggleCustomerTag = async (tag) => {
    const { activeConversation } = this.props;
    const _customer = activeConversation._customer?._id;

    if (!_customer || !tag) {
      return;
    }

    const currentTags = (activeConversation._customer?.tags || []).filter(Boolean);
    const isAdding = !currentTags.includes(tag);
    const nextTags = isAdding
      ? [...currentTags, tag]
      : currentTags.filter((currentTag) => currentTag !== tag);
    const _business =
      typeof activeConversation._business === 'object'
        ? activeConversation._business?._id
        : activeConversation._business;

    this.props.updateLoadedCustomerConversationTags({
      _customer,
      tags: nextTags,
    });

    try {
      const { customer } = await this.props.toggleCustomerTag({
        _customer,
        tag,
        isAdding,
        _business,
      });
      this.props.updateLoadedCustomerConversationTags({
        _customer,
        tags: customer.tags,
      });
    } catch {
      this.props.updateLoadedCustomerConversationTags({
        _customer,
        tags: currentTags,
      });
      this.props.displayNotification({
        message: 'Error updating customer tags',
        error: true,
      });
    }
  };

  callCustomer = async () => {
    const customer = this.props.activeConversation._customer;
    if (
      ['connecting', 'ringing', 'answered'].includes(this.props.voice.status)
    ) {
      toast.error('Finish the current call before starting another one.');
      return;
    }
    if (isCustomerOnActiveCall(this.props.voice.ongoingCalls, customer)) {
      toast.error('This customer is already on a call with another teammate.');
      return;
    }

    this.props.setCustomer(customer);
    this.props.setStatus('connecting');
    this.props.setVisible(true);
  };

  handleResolveEscalation = async (reenableAi = true) => {
    const conv = this.props.activeConversation;
    if (!conv) return;

    try {
      const _business = typeof conv._business === 'object' ? conv._business._id : conv._business;
      const res = await axios.get(`/api/ai-employee/escalations/${_business}?status=pending`);
      const ticket = res.data?.tickets?.find(
        (t) => String(t._customerConversation) === String(conv._id),
      );

      if (ticket) {
        // _business lets the API scope the request to the conversation's
        // location, which can differ from the user's own on All locations.
        await axios.put(`/api/ai-employee/escalations/${ticket._id}/resolve`, {
          reenableAi,
          _business,
        });
      }

      this.props.removeAiEscalation({
        _customerConversation: conv._id,
      });

      const stored = await this.props.resolveConversationEscalation({
        _customerConversation: conv._id,
        aiPaused: !reenableAi,
      });
      // This screen was out of date: a teammate had resolved it and kept the AI off.
      if (reenableAi && stored?.aiPaused) {
        toast('A teammate already resolved this and kept the AI off', {
          description: 'The AI is still off for this customer. Switch it on from the AI status if you want it back.',
          position: toastPosition('bottom-right'),
        });
      }
    } catch (err) {
      console.error('Failed to resolve escalation:', err);
    }
  };

  handleResetAiMemory = async () => {
    const conv = this.props.activeConversation;
    if (!conv) return;

    const _business = typeof conv._business === 'object' ? conv._business._id : conv._business;

    try {
      await axios.delete(`/api/ai-employee/support/${_business}/${conv._id}/reset`);
    } catch (err) {
      console.error('Failed to reset AI memory:', err);
    }
  };

  renderAiIndicator = () => {
    if (!hasAiEmployeeProduct(this.props.auth)) return null;

    const { aiSettings } = this.state;

    const { activeConversation } = this.props;

    // Shown while the AI replies in either period (open or closed hours).
    if (!isAiReplyOn(aiSettings)) {
      return null;
    }

    const isPaused = activeConversation?.aiPaused;
    // A hold stays until someone switches the AI back on; any other pause ends by itself.
    const pauseReason = activeConversation?.aiPauseReason;
    const pausedTooltip =
      pauseReason === 'manual_hold'
        ? 'AI is switched off for this customer and stays off until you switch it back on. Click to switch it on.'
        : pauseReason
          ? 'AI is paused because your team took over. It answers again the next time this customer writes, if the conversation is closed by then and your team has not written to them for 48 hours. Click to switch it on now.'
          : 'AI is off for this conversation. Click to switch it on.';
    const aiStatus = this.props.aiStatusByConversation?.[activeConversation?._id];
    const isThinking = aiStatus === 'thinking';
    const isEscalated =
      activeConversation?.handoffStatus === 'pending' ||
      !!this.props.escalationsByConversation?.[activeConversation?._id];
    const escalationData = this.props.escalationsByConversation?.[activeConversation?._id];
    // A Draft-mode ticket never switched the AI off, so there is nothing to resume
    // or keep off: one Resolve, and drafting goes on either way. Only a business
    // that drafts in open or closed hours has Draft tickets.
    const keepsDrafting = handoffKeepsDrafting(
      activeConversation,
      businessDraftsReplies(replyModesFrom(aiSettings)),
    );

    if (isEscalated && keepsDrafting) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors shrink-0 cursor-pointer hover:bg-accent text-amber-600 dark:text-amber-400"
            >
              <TriangleAlert className="size-3.5" />
              <span>Escalated</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="start">
            <div className="space-y-2">
              <p className="text-sm font-medium">AI requested human attention</p>
              {escalationData?.reason && (
                <p className="text-xs text-muted-foreground">{escalationData.reason}</p>
              )}
              {escalationData?.summary && (
                <p className="text-xs text-muted-foreground italic">{escalationData.summary}</p>
              )}
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                Drafting continues. The AI was not switched off for this, so it keeps writing
                drafts for your team.
              </p>
              <Button
                size="sm"
                className="w-full"
                onClick={() => this.handleResolveEscalation(true)}
              >
                <Check className="size-3.5 mr-1.5" />
                Resolve
              </Button>
              <p className="text-xs text-muted-foreground">
                Replying to the customer also resolves this.
              </p>
            </div>
          </PopoverContent>
        </Popover>
      );
    }

    if (isEscalated) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors shrink-0 cursor-pointer hover:bg-accent text-amber-600 dark:text-amber-400"
            >
              <TriangleAlert className="size-3.5" />
              <span>Escalated</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="start">
            <div className="space-y-2">
              <p className="text-sm font-medium">AI requested human attention</p>
              {escalationData?.reason && (
                <p className="text-xs text-muted-foreground">{escalationData.reason}</p>
              )}
              {escalationData?.summary && (
                <p className="text-xs text-muted-foreground italic">{escalationData.summary}</p>
              )}
              <Button
                size="sm"
                className="w-full"
                onClick={() => this.handleResolveEscalation(true)}
              >
                <Check className="size-3.5 mr-1.5" />
                Resolve & Re-enable AI
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="w-full mt-2"
                onClick={() => this.handleResolveEscalation(false)}
              >
                <Check className="size-3.5 mr-1.5" />
                Resolve & Disable AI
              </Button>
              <p className="text-xs text-muted-foreground">
                Replying to the customer also resolves this.
              </p>
              <p className="text-xs text-muted-foreground">
                Disabling keeps the AI off for this customer until you switch it back on.
              </p>
            </div>
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={this.toggleAiPause}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors shrink-0 cursor-pointer',
              isPaused
                ? 'bg-muted/70 text-muted-foreground hover:bg-muted'
                : isThinking
                  ? 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/15 dark:text-blue-400'
                  : 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-400',
            )}
          >
            <Bot className="size-3.5" />
            {isPaused ? (
              <span>AI Off</span>
            ) : isThinking ? (
              <>
                <span className="flex gap-0.5">
                  <span
                    className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </span>
                <span>Thinking</span>
              </>
            ) : (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span>Active</span>
              </>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {isPaused
            ? pausedTooltip
            : 'AI is active. Click to switch it off for this customer. It stays off until you switch it back on.'}
        </TooltipContent>
      </Tooltip>
    );
  };

  renderOverflowMenu = () => {
    const { activeConversation, folders, tags = [] } = this.props;
    const { isRead } = activeConversation;
    const { _customer } = activeConversation;
    const _isCallDisabled =
      this.props.voice.status === 'connecting' ||
      this.props.voice.status === 'ringing' ||
      this.props.voice.status === 'answered';

    const getFolderTitle = (_folder) => {
      const folder = folders?.find((f) => f._id === _folder);
      return folder ? folder.title : null;
    };

    const hasChannelChoice = _customer?.phone && _customer?.email;
    const preferredChannel = _customer?.preferredChannel || 'Phone';
    const customerTags = (_customer?.tags || []).filter(Boolean);
    const availableTags = [...new Set([...tags, ...customerTags].filter(Boolean))];
    const isPinnedForMe = (activeConversation._pinnedByUsers || []).some(
      (_user) => _user.toString() === this.props.user._id,
    );
    const isPinnedForBusiness = !!activeConversation.pinnedForBusiness;

    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="More actions" className="shrink-0">
                <EllipsisVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>More actions</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-48">
          {isRead ? (
            <DropdownMenuItem onClick={() => this.markAsUnreadConversation(activeConversation)}>
              <MailWarning className="size-4" />
              Mark as unread
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => this.markAsReadConversation(activeConversation)}>
              <MailCheck className="size-4" />
              Mark as read
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              this.handleToggleConversationPin({
                scope: 'user',
                pinned: !isPinnedForMe,
              })
            }
          >
            <Pin className="size-4" />
            {isPinnedForMe ? 'Unpin for me' : 'Pin for me'}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              this.handleToggleConversationPin({
                scope: 'business',
                pinned: !isPinnedForBusiness,
              })
            }
          >
            <Pin className="size-4" />
            {isPinnedForBusiness ? 'Unpin for everyone' : 'Pin for everyone'}
          </DropdownMenuItem>

          {this.state.groupSmsEnabled && !activeConversation.isGroup && _customer?.phone && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={this.handleAddPeople}>
                <Users className="size-4" />
                Add people
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={!_customer}>
              <Tag className="size-4" />
              Tags
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-48 max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto">
              {availableTags.length ? (
                availableTags.map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag}
                    checked={customerTags.includes(tag)}
                    onCheckedChange={() => this.handleToggleCustomerTag(tag)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    <span className="truncate">{tag}</span>
                  </DropdownMenuCheckboxItem>
                ))
              ) : (
                <DropdownMenuItem disabled>No tags available</DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <FolderSymlink className="size-4" />
              {activeConversation._folder
                ? getFolderTitle(activeConversation._folder) || 'Move to folder'
                : 'Move to folder'}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {folders?.map((folder) => (
                <DropdownMenuItem
                  key={folder._id}
                  onClick={() => this.handleFolderAction(folder._id)}
                >
                  {folder.title}
                  {activeConversation._folder === folder._id && (
                    <Check className="ml-auto size-4" />
                  )}
                </DropdownMenuItem>
              ))}
              {folders?.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={() =>
                  this.setState({
                    showCreateFolderDialog: true,
                  })
                }
              >
                <FolderPlus className="size-4" />
                Create folder
              </DropdownMenuItem>
              {activeConversation._folder && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => this.handleRemoveFolder(activeConversation._folder)}
                  >
                    Remove from folder
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {hasChannelChoice && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() =>
                  this.handleSelectPreferredChannel(
                    preferredChannel.toLowerCase() === 'phone' ? 'email' : 'phone',
                  )
                }
              >
                {preferredChannel.toLowerCase() === 'phone' ? (
                  <Mail className="size-4" />
                ) : (
                  <Phone className="size-4" />
                )}
                Switch to {preferredChannel.toLowerCase() === 'phone' ? 'email' : 'phone'}
              </DropdownMenuItem>
            </>
          )}

          {hasAiEmployeeProduct(this.props.auth) &&
            isAiReplyOn(this.state.aiSettings) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    this.setState({
                      showResetAiDialog: true,
                    });
                  }}
                >
                  <RotateCcw className="size-4" />
                  Reset AI memory
                </DropdownMenuItem>
              </>
            )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  renderNameAndPhoneBar = () => {
    const { activeConversation } = this.props;
    const isOpen = activeConversation.open;
    const customerOnActiveCall = isCustomerOnActiveCall(
      this.props.voice.ongoingCalls,
      activeConversation._customer,
    );

    // Typing sideways on a phone (keyboard up, about 110–200px left), a
    // slimmer bar leaves more of the thread above the composer.
    return (
      <div className="relative shrink-0 border-b border-border/70 bg-background px-3 py-3 sm:px-4 max-xl:[@media(max-height:560px)]:py-1.5 kb-open:max-xl:[@media(max-height:560px)]:py-0.5">
        <div
          ref={(node) => {
            this.nameAndPhoneBarRef = node;
          }}
          className="flex min-h-10 flex-wrap items-center gap-y-2 gap-x-3 max-xl:[@media(max-height:560px)]:flex-nowrap kb-open:max-xl:[@media(max-height:560px)]:min-h-9"
        >
          {/* Customer identity */}
          <div
            ref={(node) => {
              this.chatIdentityRef = node;
            }}
            className="flex items-center gap-2 min-w-0 min-h-9 max-w-full sm:max-w-[50%] max-xl:[@media(max-height:560px)]:max-w-[42%]"
          >
            {this.renderErrorWithCustomer()}
            {this.renderChatTitle()}
          </div>

          {/* Assignee + actions */}
          <div
            ref={(node) => {
              this.assigneeActionsRef = node;
            }}
            className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-x-0.5 gap-y-1 max-xl:flex-nowrap max-xl:[&>button]:shrink-0"
          >
            {this.renderAiIndicator()}
            {this.renderSelectAssignee()}
            <div className="mx-1 h-4 w-px bg-border/60" />
            {activeConversation._customer?.phone && (
              <IconButton
                toolTipTitle={
                  customerOnActiveCall ? 'Customer is already on a call' : 'Call customer'
                }
                aria-label="Call customer"
                disabled={
                  customerOnActiveCall ||
                  this.props.voice.status === 'connecting' ||
                  this.props.voice.status === 'ringing' ||
                  this.props.voice.status === 'answered'
                }
                onClick={() => {
                  if (this.state.hasVoiceProduct) {
                    this.callCustomer();
                  } else {
                    this.setState({
                      showVoiceUpsell: true,
                    });
                  }
                }}
              >
                <Phone className="size-4" />
              </IconButton>
            )}
            <IconButton
              toolTipTitle={isOpen ? 'Close conversation' : 'Reopen'}
              aria-label={isOpen ? 'close' : 'open'}
              onClick={() =>
                isOpen
                  ? this.closeConversation(activeConversation)
                  : this.openConversation(activeConversation)
              }
            >
              {isOpen ? <CheckCircle className="size-4" /> : <MailOpen className="size-4" />}
            </IconButton>

            {this.renderOverflowMenu()}

            {!this.props.showConversationDetails && (
              <IconButton
                variant="ghost"
                onClick={this.props.toggleShowConversationDetails}
                toolTipTitle="Show Conversation Details"
                aria-label="Show Conversation Details"
                className="text-muted-foreground"
              >
                <PanelRightOpen className="size-4" />
              </IconButton>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ---- Phones -------------------------------------------------------------
  // A native conversation header (back, who, call, "…") replaces the desktop
  // bar below md. Everything the desktop bar and its menus offer lives in one
  // action sheet behind "…", so nothing depends on hover.

  getMobileIdentity = () => {
    const { activeConversation, isActivated } = this.props;
    if (activeConversation?.isGroup) {
      const customers = activeConversation._customers || [];
      const name =
        activeConversation.chatName ||
        customers.map((c) => maskPhone(c?.name || c?.phone, isActivated)).filter(Boolean).join(', ') ||
        maskPhone(activeConversation.title, isActivated) ||
        'Group';
      return { name, subtitle: `${customers.length} people`, isGroup: true };
    }

    const { title, _customer } = activeConversation || {};
    const name = maskPhone(title || _customer?.name || 'No name', isActivated) || 'No name';
    let subtitle = '';
    if (_customer?.phone || _customer?.preferredChannel === 'phone') {
      subtitle = maskPhone(formatPhoneForTitleBar(_customer?.phone), isActivated);
    }
    if (
      (_customer?.email && !_customer?.phone) ||
      (_customer?.email && _customer?.preferredChannel === 'email')
    ) {
      subtitle = _customer?.email;
    }
    if (_customer?.facebookUserId && !_customer?.phone) subtitle = 'Facebook Messenger';
    if (_customer?.instagramUserId && !_customer?.phone) subtitle = 'Instagram';
    if (_customer?.googleConversationId && !_customer?.phone) subtitle = 'Google Business Messenger';
    return { name, subtitle, isGroup: false };
  };

  // The AI Employee's state for this conversation, or null when it isn't replying.
  getMobileAiState = () => {
    if (!hasAiEmployeeProduct(this.props.auth) || !isAiReplyOn(this.state.aiSettings)) return null;
    const { activeConversation } = this.props;
    const isEscalated =
      activeConversation?.handoffStatus === 'pending' ||
      !!this.props.escalationsByConversation?.[activeConversation?._id];
    const isThinking = this.props.aiStatusByConversation?.[activeConversation?._id] === 'thinking';
    return {
      isPaused: !!activeConversation?.aiPaused,
      pauseReason: activeConversation?.aiPauseReason,
      isThinking,
      isEscalated,
      escalation: this.props.escalationsByConversation?.[activeConversation?._id],
      keepsDrafting: handoffKeepsDrafting(
        activeConversation,
        businessDraftsReplies(replyModesFrom(this.state.aiSettings)),
      ),
    };
  };

  openMobileDetails = () => {
    const open = this.props.onOpenDetails || this.props.toggleShowConversationDetails;
    open?.();
  };

  startMobileCall = () => {
    if (this.state.hasVoiceProduct) {
      this.callCustomer();
    } else {
      this.setState({ showVoiceUpsell: true });
    }
  };

  // Closing from a phone returns to the list, like archiving in Mail. Undo reopens.
  closeConversationFromMobile = () => {
    const conversation = this.props.activeConversation;
    this.closeConversation(conversation);
    toast('Conversation closed', {
      position: 'top-center',
      action: {
        label: 'Undo',
        onClick: () => this.props.toggleConversationOpen({ conversation, open: true }),
      },
    });
    this.props.onMobileBack?.();
  };

  renderMobileHeader = () => {
    const { activeConversation, voice } = this.props;
    const { name, subtitle, isGroup } = this.getMobileIdentity();
    const _customer = activeConversation._customer;
    const ai = this.getMobileAiState();
    const customerOnActiveCall = isCustomerOnActiveCall(voice.ongoingCalls, _customer);
    const callDisabled =
      customerOnActiveCall || ['connecting', 'ringing', 'answered'].includes(voice.status);
    const optedOut = !isGroup && _customer && _customer.active === false;
    const customerMissing = !isGroup && !_customer;
    const aiLabel = ai
      ? ai.isEscalated
        ? 'Needs a person'
        : ai.isPaused
          ? 'AI off'
          : ai.isThinking
            ? 'AI thinking…'
            : 'AI on'
      : null;

    const statusText = customerMissing
      ? 'There may be an error with this customer. Refresh the page or contact support.'
      : 'This customer opted out of messages. They can text START to your texting number to get them again.';

    // WebchatDashboard hides the tab bar while the thread is on screen; this
    // component stays mounted behind the list, so it must not hide it itself.
    // A phone held sideways with the keyboard up has 130–170px left, so the
    // bar drops to one 44px line (no subtitle) and some of the thread shows.
    // The banners under it fold into header buttons then (different icons, so
    // colour isn't the only difference): the escalation one opens the AI
    // sheet, the warning one drops the keyboard so the full banner shows
    // again. Either way focus moves on to the banner, as the button hides.
    return (
      <>
        <header className="relative z-20 shrink-0 border-b border-border/60 bg-background/90 backdrop-blur-xl supports-[backdrop-filter]:bg-background/75">
          <div className="flex h-14 items-center gap-0.5 pl-0.5 pr-1 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:h-11">
            <MobileBackButton
              onClick={this.props.onMobileBack}
              fallback={getCustomerConversationBasePath(window.location.pathname)}
              className="pr-0"
            />
            <button
              type="button"
              onClick={this.openMobileDetails}
              aria-label={`${name}. Contact details`}
              className="conversation-enter flex h-12 min-w-0 flex-1 items-center gap-2.5 rounded-xl pr-1 text-left active:bg-accent/60 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:h-11 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:gap-2"
              key={activeConversation._id}
            >
              {isGroup ? (
                <Avatar className="size-9 shrink-0 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:size-7">
                  <AvatarFallback
                    className="text-sm font-medium text-white"
                    style={{ backgroundColor: getAvatarColor(name || activeConversation._id) }}
                  >
                    <Users className="size-4" />
                  </AvatarFallback>
                </Avatar>
              ) : (
                <CustomerAvatar
                  customer={
                    _customer && {
                      ..._customer,
                      _business: _customer._business || activeConversation._business,
                    }
                  }
                  className="size-9 shrink-0 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:size-7"
                  fallbackClassName="text-sm font-medium kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:text-xs"
                  fallbackName={name}
                  colorSeed={
                    _customer?.name || activeConversation.title || _customer?._id || activeConversation._id
                  }
                />
              )}
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-base font-semibold leading-tight">{name}</span>
                <span className="flex min-w-0 items-center gap-1 text-xs leading-tight text-muted-foreground kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:hidden">
                  {subtitle && <span className="truncate">{subtitle}</span>}
                  {aiLabel && (
                    <span
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1',
                        ai.isEscalated
                          ? 'text-amber-600 dark:text-amber-400'
                          : ai.isPaused
                            ? ''
                            : ai.isThinking
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-emerald-700 dark:text-emerald-400',
                      )}
                    >
                      {subtitle && <span aria-hidden className="text-muted-foreground">·</span>}
                      <Bot aria-hidden className="size-3" />
                      {aiLabel}
                    </span>
                  )}
                </span>
              </span>
            </button>
            {(customerMissing || optedOut) && (
              <MobileHeaderButton
                aria-label={statusText}
                onClick={() => {
                  document.activeElement?.blur?.();
                  focusBannerOnceShown(() => this.mobileStatusBannerRef);
                }}
                className="hidden text-destructive dark:text-red-400 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:inline-flex"
              >
                {optedOut ? <MessageSquareOff /> : <TriangleAlert />}
              </MobileHeaderButton>
            )}
            {ai?.isEscalated && (
              <MobileHeaderButton
                aria-label="AI asked for a person. Review"
                aria-haspopup="dialog"
                onClick={() => this.setState({ mobileAiOpen: true })}
                className="hidden text-amber-600 dark:text-amber-400 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:inline-flex"
              >
                <Bot />
              </MobileHeaderButton>
            )}
            {!isGroup && _customer?.phone && (
              <MobileHeaderButton
                aria-label={customerOnActiveCall ? 'Customer is already on a call' : `Call ${name}`}
                disabled={callDisabled}
                onClick={this.startMobileCall}
              >
                <Phone />
              </MobileHeaderButton>
            )}
            <MobileHeaderButton
              aria-label="Conversation actions"
              aria-haspopup="dialog"
              onClick={() => this.setState({ mobileActionsOpen: true })}
            >
              <Ellipsis />
            </MobileHeaderButton>
          </div>
        </header>
        {(customerMissing || optedOut) && (
          <div
            ref={(node) => {
              this.mobileStatusBannerRef = node;
            }}
            role="status"
            tabIndex={-1}
            className="flex shrink-0 items-start gap-2 border-b border-destructive/20 bg-destructive/10 px-4 py-2 text-[13px] leading-snug text-destructive outline-none dark:text-red-400 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:hidden"
          >
            <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
            <span>{statusText}</span>
          </div>
        )}
        {ai?.isEscalated && (
          <button
            ref={(node) => {
              this.mobileEscalationBannerRef = node;
            }}
            type="button"
            onClick={() => this.setState({ mobileAiOpen: true })}
            className="flex min-h-11 shrink-0 items-center gap-2 border-b border-amber-500/20 bg-amber-500/10 px-4 py-2 text-left text-[13px] leading-snug text-amber-800 active:bg-amber-500/20 dark:text-amber-300 kb-open:[@media(orientation:landscape)_and_(max-height:560px)]:hidden"
          >
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="font-medium">AI asked for a person.</span>{' '}
              {ai.escalation?.reason || 'Review and resolve when you have answered.'}
            </span>
            <span className="shrink-0 font-medium">Review</span>
          </button>
        )}
      </>
    );
  };

  getMobileAssignSections = () => {
    const { options, selectedValues } = this.getAssigneeSelection();
    const toggle = (value) => {
      const next = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      this.handleSelectAssignees(next);
    };
    return [
      {
        id: 'none',
        items: [
          {
            id: 'unassigned',
            label: 'Unassigned',
            icon: User,
            checked: selectedValues.length === 0,
            keepOpen: true,
            onSelect: () => this.handleSelectAssignees([]),
          },
        ],
      },
      ...options
        .filter((group) => group.options.length > 0)
        .map((group) => ({
          id: group.heading,
          title: group.heading,
          items: group.options.map((option) => ({
            id: option.value,
            label: option.label,
            icon: option.value.startsWith('DEPARTMENT_') ? Users : UserRound,
            checked: selectedValues.includes(option.value),
            keepOpen: true,
            onSelect: () => toggle(option.value),
          })),
        })),
    ];
  };

  getMobileAiPage = (ai) => {
    if (!ai) return null;
    const explanation = ai.isEscalated
      ? ai.keepsDrafting
        ? 'The AI asked for a person to take over. It was not switched off, so it keeps writing drafts for your team. Replying to the customer also resolves this.'
        : 'The AI asked for a person to take over. Replying to the customer also resolves this. Keeping the AI off holds it until you switch it back on.'
      : ai.isPaused
        ? ai.pauseReason === 'manual_hold'
          ? 'The AI is switched off for this customer and stays off until you switch it back on.'
          : ai.pauseReason
            ? 'The AI is paused because your team took over. It answers again the next time this customer writes, if the conversation is closed by then and your team has not written to them for 48 hours.'
            : 'The AI is off for this conversation.'
        : 'The AI is answering this customer. Switching it off keeps it off until you switch it back on.';

    const items = ai.isEscalated
      ? ai.keepsDrafting
        ? [{ id: 'resolve', label: 'Resolve', icon: Check, onSelect: () => this.handleResolveEscalation(true) }]
        : [
            {
              id: 'resolve-on',
              label: 'Resolve and switch AI on',
              icon: Check,
              onSelect: () => this.handleResolveEscalation(true),
            },
            {
              id: 'resolve-off',
              label: 'Resolve and keep AI off',
              icon: PowerOff,
              onSelect: () => this.handleResolveEscalation(false),
            },
          ]
      : [
          {
            id: 'toggle',
            label: ai.isPaused ? 'Switch AI on for this customer' : 'Switch AI off for this customer',
            icon: ai.isPaused ? Bot : PowerOff,
            onSelect: this.toggleAiPause,
          },
        ];

    return {
      id: 'ai',
      title: 'AI Employee',
      content: (
        <div className="mb-3 space-y-1.5 rounded-xl bg-background px-4 py-3 text-[15px] leading-snug dark:bg-zinc-900">
          {ai.isEscalated && ai.escalation?.reason && <p className="font-medium">{ai.escalation.reason}</p>}
          {ai.isEscalated && ai.escalation?.summary && (
            <p className="text-[13px] italic text-muted-foreground">{ai.escalation.summary}</p>
          )}
          <p className="text-[13px] text-muted-foreground">{explanation}</p>
        </div>
      ),
      sections: [
        { id: 'ai-actions', items },
        {
          id: 'ai-reset',
          items: [
            {
              id: 'reset',
              label: 'Reset AI memory',
              icon: RotateCcw,
              description: 'A fresh start for this conversation only',
              onSelect: () => this.setState({ showResetAiDialog: true }),
            },
          ],
        },
      ],
    };
  };

  renderMobileActionSheet = () => {
    const { activeConversation, folders, tags = [] } = this.props;
    const { _customer, isRead } = activeConversation;
    const isOpen = activeConversation.open;
    const { assigneeNames } = this.getAssigneeSelection();
    const ai = this.getMobileAiState();
    const customerTags = (_customer?.tags || []).filter(Boolean);
    const availableTags = [...new Set([...tags, ...customerTags].filter(Boolean))];
    const isPinnedForMe = (activeConversation._pinnedByUsers || []).some(
      (_user) => _user.toString() === this.props.user._id,
    );
    const isPinnedForBusiness = !!activeConversation.pinnedForBusiness;
    const folderTitle = folders?.find((f) => f._id === activeConversation._folder)?.title;
    const hasChannelChoice = _customer?.phone && _customer?.email;
    const preferredChannel = (_customer?.preferredChannel || 'Phone').toLowerCase();
    const aiLabel = ai
      ? ai.isEscalated
        ? 'Needs a person'
        : ai.isPaused
          ? 'Off'
          : 'On'
      : null;

    const sections = [
      {
        id: 'status',
        items: [
          {
            id: 'assign',
            label: 'Assign',
            icon: UserRound,
            trailing: (
              <span className="block max-w-[9.5rem] truncate">
                {assigneeNames.length ? assigneeNames.join(', ') : 'Unassigned'}
              </span>
            ),
            page: 'assign',
          },
          ...(ai
            ? [{ id: 'ai', label: 'AI Employee', icon: Bot, trailing: aiLabel, page: 'ai' }]
            : []),
          isRead
            ? {
                id: 'unread',
                label: 'Mark as unread',
                icon: MailWarning,
                onSelect: () => this.markAsUnreadConversation(activeConversation),
              }
            : {
                id: 'read',
                label: 'Mark as read',
                icon: MailCheck,
                onSelect: () => this.markAsReadConversation(activeConversation),
              },
          isOpen
            ? {
                id: 'close',
                label: 'Close conversation',
                icon: CheckCircle,
                onSelect: this.closeConversationFromMobile,
              }
            : {
                id: 'reopen',
                label: 'Reopen conversation',
                icon: MailOpen,
                onSelect: () => this.openConversation(activeConversation),
              },
        ],
      },
      {
        id: 'pin',
        items: [
          {
            id: 'pin-me',
            label: isPinnedForMe ? 'Unpin for me' : 'Pin for me',
            icon: Pin,
            onSelect: () => this.handleToggleConversationPin({ scope: 'user', pinned: !isPinnedForMe }),
          },
          {
            id: 'pin-all',
            label: isPinnedForBusiness ? 'Unpin for everyone' : 'Pin for everyone',
            icon: Pin,
            onSelect: () =>
              this.handleToggleConversationPin({ scope: 'business', pinned: !isPinnedForBusiness }),
          },
        ],
      },
      {
        id: 'organise',
        items: [
          {
            id: 'tags',
            label: 'Tags',
            icon: Tag,
            disabled: !_customer,
            trailing: customerTags.length ? String(customerTags.length) : undefined,
            page: 'tags',
          },
          {
            id: 'folder',
            label: 'Folder',
            icon: FolderSymlink,
            trailing: <span className="block max-w-[9.5rem] truncate">{folderTitle || 'None'}</span>,
            page: 'folder',
          },
          ...(hasChannelChoice
            ? [
                {
                  id: 'channel',
                  label: `Switch to ${preferredChannel === 'phone' ? 'email' : 'phone'}`,
                  icon: preferredChannel === 'phone' ? Mail : Phone,
                  onSelect: () =>
                    this.handleSelectPreferredChannel(preferredChannel === 'phone' ? 'email' : 'phone'),
                },
              ]
            : []),
          ...(this.state.groupSmsEnabled && !activeConversation.isGroup && _customer?.phone
            ? [{ id: 'add-people', label: 'Add people', icon: Users, onSelect: this.handleAddPeople }]
            : []),
          ...(activeConversation.isGroup
            ? [
                {
                  id: 'rename',
                  label: 'Rename group',
                  icon: Pencil,
                  onSelect: () =>
                    this.setState({
                      mobileRenameOpen: true,
                      chatNameDraft: activeConversation.chatName || '',
                    }),
                },
              ]
            : []),
          {
            id: 'details',
            label: 'Contact details',
            icon: PanelRightOpen,
            onSelect: this.openMobileDetails,
          },
        ],
      },
    ];

    const pages = [
      {
        id: 'assign',
        title: 'Assign',
        searchable: true,
        searchPlaceholder: 'Search people and departments',
        emptyText: 'No matches',
        sections: this.getMobileAssignSections(),
      },
      {
        id: 'tags',
        title: 'Tags',
        searchable: availableTags.length > 8,
        sections: availableTags.length
          ? [
              {
                id: 'tags',
                items: availableTags.map((tag) => ({
                  id: tag,
                  label: tag,
                  checked: customerTags.includes(tag),
                  keepOpen: true,
                  onSelect: () => this.handleToggleCustomerTag(tag),
                })),
              },
            ]
          : [],
        content: availableTags.length ? null : (
          <p className="px-3 py-6 text-center text-[15px] text-muted-foreground">
            No tags yet. Create tags in Settings.
          </p>
        ),
      },
      {
        id: 'folder',
        title: 'Folder',
        sections: [
          ...(folders?.length
            ? [
                {
                  id: 'folders',
                  items: folders.map((folder) => ({
                    id: folder._id,
                    label: folder.title,
                    checked: activeConversation._folder === folder._id,
                    onSelect: () => this.handleFolderAction(folder._id),
                  })),
                },
              ]
            : []),
          {
            id: 'folder-actions',
            items: [
              {
                id: 'create-folder',
                label: 'Create folder',
                icon: FolderPlus,
                onSelect: () => this.setState({ showCreateFolderDialog: true }),
              },
              ...(activeConversation._folder
                ? [
                    {
                      id: 'remove-folder',
                      label: 'Remove from folder',
                      icon: FolderMinus,
                      onSelect: () => this.handleRemoveFolder(activeConversation._folder),
                    },
                  ]
                : []),
            ],
          },
        ],
      },
      ...(ai ? [this.getMobileAiPage(ai)] : []),
    ];

    return (
      <ActionSheet
        open={this.state.mobileActionsOpen}
        onOpenChange={(open) => this.setState({ mobileActionsOpen: open })}
        title={this.getMobileIdentity().name}
        sections={sections}
        pages={pages}
        cancelLabel="Done"
      />
    );
  };

  // The escalation banner opens the AI page on its own. Opened from the header
  // button with the keyboard up, focus goes back to the banner that replaced it.
  renderMobileAiSheet = () => {
    const page = this.getMobileAiPage(this.getMobileAiState());
    if (!page) return null;
    return (
      <ActionSheet
        open={this.state.mobileAiOpen}
        onOpenChange={(open) => {
          this.setState({ mobileAiOpen: open });
          if (!open) focusBannerOnceShown(() => this.mobileEscalationBannerRef);
        }}
        title="AI Employee"
        header={page.content}
        sections={page.sections}
      />
    );
  };

  renderMobileRenameDialog = () => (
    <Dialog
      open={this.state.mobileRenameOpen}
      onOpenChange={(open) => this.setState({ mobileRenameOpen: open })}
    >
      <DialogContent mobileVariant="dialog">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            this.setState({ mobileRenameOpen: false });
            this.saveChatName();
          }}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>Rename group</DialogTitle>
            <DialogDescription>Everyone on your team sees this name.</DialogDescription>
          </DialogHeader>
          <Input
            value={this.state.chatNameDraft || ''}
            onChange={(e) => this.setState({ chatNameDraft: e.target.value })}
            placeholder="Name this group chat"
            aria-label="Group name"
            className="h-11 text-base md:text-sm"
            autoComplete="off"
            enterKeyHint="done"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => this.setState({ mobileRenameOpen: false })}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  renderChatBox = () => (
    <ChatBox
      generateAIResponse={this.generateAIResponse}
      sendMessage={this.sendMessage}
      sendNote={this.sendNote}
      sendMessageToNewCustomer={this.sendMessageToNewCustomer}
      setPaymentSendInProgress={this.props.setPaymentSendInProgress}
      setLoadingNewMessage={this.props.setLoadingNewMessage}
      refreshScheduledMessages={this.props.refreshScheduledMessages}
      name={this.state.name}
      phoneEmail={this.state.phoneEmail}
      _location={this.state._location}
      employee={this.getEmployeeName()}
      resetNameAndPhoneEmail={this.resetNameAndPhoneEmail}
      activeConversation={this.props.activeConversation}
      activeCustomer={this.props.activeCustomer}
      isMobile={!!this.props.isMobile}
    />
  );

  render() {
    const isConversationSelected = this.props.activeConversation?._id;
    return (
      <div
        className={cn(
          'flex min-w-0 min-h-0 flex-1 flex-col h-full font-sans',
          isConversationSelected && !this.props.isMobile && SIDEWAYS_ROWS,
        )}
      >
        {isConversationSelected &&
          (this.props.isMobile ? this.renderMobileHeader() : this.renderNameAndPhoneBar())}
        {isConversationSelected ? this.renderChatFeed() : this.renderNewMessage()}
        {isConversationSelected && this.renderChatBox()}
        {this.props.isMobile && isConversationSelected && this.renderMobileActionSheet()}
        {this.props.isMobile && isConversationSelected && this.renderMobileAiSheet()}
        {this.props.isMobile && isConversationSelected && this.renderMobileRenameDialog()}
        <CreateFolderDialog
          open={this.state.showCreateFolderDialog}
          setOpen={(open) => this.setState({ showCreateFolderDialog: open })}
        />

        <AlertDialog
          open={this.state.showVoiceUpsell}
          onOpenChange={(open) => this.setState({ showVoiceUpsell: open })}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Voice Calling</AlertDialogTitle>
              <AlertDialogDescription>
                Call your customers directly from Chekkit with our Voice Calling feature. Contact us
                to add voice calling to your plan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction>Got it</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={this.state.showResetAiDialog}
          onOpenChange={(open) => this.setState({ showResetAiDialog: open })}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset AI memory for this conversation</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  This gives the AI a fresh start for this conversation. It will clear what the AI
                  remembers about previous messages here, so it can approach the conversation
                  without any prior context.
                </span>
                <span className="block">
                  This only affects this one conversation &mdash; your AI&apos;s training, knowledge
                  base, and all other conversations are not changed.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={this.handleResetAiMemory}>
                Reset AI memory
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    users: state.users,
    user: state.auth.user,
    isActivated: canExploreSignupDashboard(state.auth) ? true : state.auth.activated,
    brandColor: state.brandColor,
    departments: state.departments,
    auth: state.auth,
    businessGroup: state.businessGroup,
    folders: state.folders,
    tags: state.tags,
    voice: state.voice,
    aiStatusByConversation: state.aiEmployee.statusByConversation,
    escalationsByConversation: state.aiEmployee.escalationsByConversation,
  };
}

export default connect(
  mapStateToProps,
  {
    addFolderToConversation,
    displayNotification,
    removeFolderFromConversation,
    removeAiEscalation,
    resolveConversationEscalation,
    setCustomer,
    setStatus,
    setVisible,
    toggleConversationAiPause,
    toggleConversationOpen,
    toggleConversationPin,
    toggleCustomerTag,
    updateConversationAssignees,
    updateLoadedCustomerConversationTags,
  },
  null,
  { forwardRef: true },
)(Messages);
